import type { ComponentType } from 'react'
import { ALL_SECTIONS_ON, type Section, type Sections } from '../../../shared/sections'
import { CalendarIcon, HomeIcon, MomentsIcon, PeopleIcon, PhotosIcon, RecipesIcon, type PlaceIconProps } from './icons'

// The sections of a space, the way notesfrom lists a journal's: the sidebar, its folded rail and the phone's tabs
// all draw this one list. Plain words: Home, People, Photos, Moments, Recipes, Calendar, each added here once its page
// exists.
// Home is the circle's front page, where the app opens. A book, its recipes and the cookbooks live under Recipes; a
// person's page under People; an album and its photos under Photos; a moment under Moments. A section the owner has
// switched off isn't listed.
export type Place = {
  key: Section
  label: string
  icon: ComponentType<PlaceIconProps>
  to: string
  active: boolean
  // Something there is waiting for you (someone asking to connect, on People): a dot, never a number.
  dot?: boolean
}

export function places(
  familyId: string,
  pathname: string,
  inFamily: boolean,
  sections: Sections = ALL_SECTIONS_ON,
): Place[] {
  const base = `/circle/${familyId}`
  const all: Place[] = [
    { key: 'home', label: 'Home', icon: HomeIcon, to: base, active: pathname === base },
    {
      key: 'people',
      label: 'People',
      icon: PeopleIcon,
      to: `${base}/people`,
      active: pathname.startsWith(`${base}/people`) || (inFamily && pathname.startsWith('/person/')),
    },
    {
      key: 'photos',
      label: 'Photos',
      icon: PhotosIcon,
      to: `${base}/photos`,
      active: pathname.startsWith(`${base}/photos`) || (inFamily && /^\/(album|photo)\//.test(pathname)),
    },
    {
      key: 'moments',
      label: 'Moments',
      icon: MomentsIcon,
      to: `${base}/moments`,
      active: pathname.startsWith(`${base}/moments`) || (inFamily && pathname.startsWith('/moment/')),
    },
    {
      key: 'recipes',
      label: 'Recipes',
      icon: RecipesIcon,
      to: `${base}/recipes`,
      active: pathname.startsWith(`${base}/recipes`) || (inFamily && /^\/(book|recipe|cookbook)\//.test(pathname)),
    },
    {
      key: 'calendar',
      label: 'Calendar',
      icon: CalendarIcon,
      to: `${base}/calendar`,
      active: pathname.startsWith(`${base}/calendar`),
    },
  ]
  return all.filter((place) => sections[place.key])
}

// Everyone's own sections, the same tabs as a circle's: what everyone you know shares with Everyone, a section at a
// time, and the people you know. A thing opened from here keeps these tabs (`fromHere`), so moving between sections
// stays in Everyone until you pick a circle.
export const EVERYONE = '/everyone'
export function everyonePlaces(pathname: string, fromHere: boolean, sections: Sections = ALL_SECTIONS_ON): Place[] {
  const at = (path: string) => pathname === path || pathname.startsWith(`${path}/`)
  const all: Place[] = [
    { key: 'home', label: 'Home', icon: HomeIcon, to: EVERYONE, active: pathname === EVERYONE },
    {
      key: 'people',
      label: 'People',
      icon: PeopleIcon,
      to: `${EVERYONE}/people`,
      active: at(`${EVERYONE}/people`) || (fromHere && pathname.startsWith('/person/')),
    },
    {
      key: 'photos',
      label: 'Photos',
      icon: PhotosIcon,
      to: `${EVERYONE}/photos`,
      active: at(`${EVERYONE}/photos`) || (fromHere && /^\/(album|photo)\//.test(pathname)),
    },
    {
      key: 'moments',
      label: 'Moments',
      icon: MomentsIcon,
      to: `${EVERYONE}/moments`,
      active: at(`${EVERYONE}/moments`) || (fromHere && pathname.startsWith('/moment/')),
    },
    {
      key: 'recipes',
      label: 'Recipes',
      icon: RecipesIcon,
      to: `${EVERYONE}/recipes`,
      active: at(`${EVERYONE}/recipes`) || (fromHere && /^\/(book|recipe|cookbook)\//.test(pathname)),
    },
    {
      key: 'calendar',
      label: 'Calendar',
      icon: CalendarIcon,
      to: `${EVERYONE}/calendar`,
      active: at(`${EVERYONE}/calendar`),
    },
  ]
  // Everyone's Home is always there: it's where Everyone opens.
  return all.filter((place) => place.key === 'home' || sections[place.key])
}
